/**
 * @fileoverview Generic single-metric KPI tile.
 * @description A richer standalone alternative to `KpiStrip`'s compact multi-tile row — a bigger
 * value, an optional trend row (up/down/flat arrow + text, colored by whether that direction is
 * actually good), and an optional caption below a divider. Ported from the InsightBox Banking
 * design system's own `KpiCard`, generalized (icons inlined to match this app's own icon
 * convention instead of `lucide-react`, "navy" tone renamed "accent" to match this app's token
 * name) since any module wanting a bigger metric tile than `KpiStrip` provides needs the same
 * shape.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import Skeleton from '@/shared/components/atoms/Skeleton'

interface TrendInfo {
  direction: 'up' | 'down' | 'flat'
  text: string
  positiveIsGood?: boolean
}

type Tone = 'default' | 'accent' | 'green' | 'red' | 'blue'
type CaptionTone = 'success' | 'warning' | 'neutral'

interface KpiCardProps {
  label: string
  value: string
  sublabel?: string
  trend?: TrendInfo
  highlight?: boolean
  layout?: 'standard' | 'trajectory'
  caption?: string
  captionTone?: CaptionTone
  tone?: Tone
  dense?: boolean
  loading?: boolean
}

const TONE_CARD_CLASS: Record<Tone, string> = {
  default: 'border-border bg-white',
  accent: 'border-accent bg-accent',
  green: 'border-green-100 bg-green-50',
  red: 'border-red-100 bg-red-50',
  blue: 'border-blue-100 bg-blue-50',
}

const TONE_VALUE_CLASS: Record<Tone, string> = {
  default: 'text-text',
  accent: 'text-white',
  green: 'text-green-700',
  red: 'text-red-700',
  blue: 'text-blue-700',
}

const TONE_LABEL_CLASS: Record<Tone, string> = {
  default: 'text-label',
  accent: 'text-white/70',
  green: 'text-text',
  red: 'text-text',
  blue: 'text-text',
}

const CAPTION_TONE_CLASS: Record<CaptionTone, string> = {
  success: 'text-success',
  warning: 'text-warning',
  neutral: 'text-label',
}

const TREND_ICON_PATH: Record<TrendInfo['direction'], string> = {
  up: 'M12 19V5M5 12l7-7 7 7',
  down: 'M12 5v14M5 12l7 7 7-7',
  flat: 'M5 12h14',
}

/**
 * @description The trend row shared by both layouts — a direction arrow + text, colored green/red
 * by whether that direction is actually favorable (`positiveIsGood`), or muted for "flat".
 * @param {{ trend: TrendInfo; onAccent: boolean }} props - The trend to render, and whether it's
 * sitting on the tinted "accent" tone (needs a lighter muted color for "flat").
 */
function TrendRow({ trend, onAccent }: { trend: TrendInfo; onAccent: boolean }) {
  const trendGood =
    trend.direction === 'flat'
      ? null
      : (trend.direction === 'up') === (trend.positiveIsGood ?? true)
  const colorClassName =
    trend.direction === 'flat'
      ? onAccent
        ? 'text-white/70'
        : 'text-label'
      : trendGood
        ? 'text-success'
        : 'text-error'

  return (
    <div className={`flex items-center gap-1 text-xs font-medium ${colorClassName}`}>
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={TREND_ICON_PATH[trend.direction]} />
      </svg>
      <span>{trend.text}</span>
    </div>
  )
}

/**
 * @component
 * @param {KpiCardProps} props - The tile's label/value plus its optional trend/caption content and
 * layout/tone/loading switches.
 * @param {string} props.label - The small uppercase label above the value.
 * @param {string} props.value - The big headline value.
 * @param {string} [props.sublabel] - A line under the value (e.g. "35 of 48 targets").
 * @param {TrendInfo} [props.trend] - An up/down/flat direction + text, shown below a divider.
 * @param {boolean} [props.highlight] - Defaults the tone to "accent" (tinted background) when no
 * explicit `tone` is given. Off by default.
 * @param {'standard' | 'trajectory'} [props.layout] - "standard" (default): value → sublabel →
 * divider → trend. "trajectory": value → trend/sublabel → divider → caption.
 * @param {string} [props.caption] - Trajectory-layout-only text below its own divider.
 * @param {CaptionTone} [props.captionTone] - Caption text color, independent of the card's tone.
 * Defaults to "neutral".
 * @param {Tone} [props.tone] - Tinted card background + value color. Defaults to "accent" when
 * `highlight` is set, else "default".
 * @param {boolean} [props.dense] - Tighter padding/type scale. Off by default.
 * @param {boolean} [props.loading] - Shows a shimmer in place of value/sublabel/trend/caption,
 * keeping the label and card frame in place so layout doesn't jump. Off by default.
 */
function KpiCard({
  label,
  value,
  sublabel,
  trend,
  highlight = false,
  layout = 'standard',
  caption,
  captionTone = 'neutral',
  tone,
  dense = false,
  loading = false,
}: KpiCardProps) {
  const resolvedTone: Tone = tone ?? (highlight ? 'accent' : 'default')
  const isAccent = resolvedTone === 'accent'
  const dividerClassName = isAccent ? 'border-white/15' : 'border-border'

  return (
    <div
      className={`flex flex-col justify-between rounded-2xl border shadow-card ${dense ? 'p-3.5' : 'p-5'} ${TONE_CARD_CLASS[resolvedTone]} ${isAccent ? 'text-white' : ''}`}
    >
      <p
        className={`text-[11px] font-semibold tracking-wide uppercase ${TONE_LABEL_CLASS[resolvedTone]}`}
      >
        {label}
      </p>

      {loading ? (
        <Skeleton width={96} height={28} />
      ) : (
        <p
          className={`font-extrabold leading-tight ${dense ? 'mt-1 text-xl' : 'mt-2 text-[28px]'} ${TONE_VALUE_CLASS[resolvedTone]}`}
        >
          {value}
        </p>
      )}

      {layout === 'standard' && (
        <>
          {loading ? (
            <Skeleton width={80} height={12} />
          ) : (
            sublabel && (
              <p className={`mt-1 text-xs ${isAccent ? 'text-white/70' : 'text-label'}`}>
                {sublabel}
              </p>
            )
          )}
          {(trend || loading) && (
            <div
              className={`border-t ${dividerClassName} ${dense ? 'mt-1.5 pt-1.5' : 'mt-3 pt-2.5'}`}
            >
              {loading ? (
                <Skeleton width={64} height={12} />
              ) : (
                trend && <TrendRow trend={trend} onAccent={isAccent} />
              )}
            </div>
          )}
        </>
      )}

      {layout === 'trajectory' && (
        <>
          {loading ? (
            <Skeleton width={80} height={12} />
          ) : (
            sublabel && (
              <p className={`mt-1 text-xs ${isAccent ? 'text-white/70' : 'text-label'}`}>
                {sublabel}
              </p>
            )
          )}
          {(trend || loading) && (
            <div className={dense ? 'mt-1' : 'mt-2'}>
              {loading ? (
                <Skeleton width={64} height={12} />
              ) : (
                trend && <TrendRow trend={trend} onAccent={isAccent} />
              )}
            </div>
          )}
          {(caption || loading) && (
            <p
              className={`border-t text-xs font-medium ${dividerClassName} ${dense ? 'mt-1.5 pt-1.5' : 'mt-3 pt-2.5'} ${
                isAccent && captionTone === 'neutral'
                  ? 'text-white/70'
                  : CAPTION_TONE_CLASS[captionTone]
              }`}
            >
              {loading ? <Skeleton width={112} height={12} /> : caption}
            </p>
          )}
        </>
      )}
    </div>
  )
}

export default KpiCard
